import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import { X, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SUIT_SYMBOL, SUIT_COLOR, SUIT_LABEL } from "@/lib/suits";

const BRAND = "#F5D400";
const ord = (n) => (n ? `${n}°` : "—");

export const ShareResultModal = ({ open, onClose, athleteId }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (open && athleteId) {
      setRes(null);
      api.get(`/athlete-result/${athleteId}`).then((r) => setRes(r.data)).catch(() => {});
      api.get("/event").then((r) => setEvent(r.data)).catch(() => {});
    }
  }, [open, athleteId]);

  if (!open || !athleteId) return null;

  const suitColor = res && SUIT_COLOR[res.suit] === "#111111" ? "#FFFFFF" : (res ? SUIT_COLOR[res.suit] : "#fff");
  const isWinner = res && !!res.title_big;
  const bannerSize = res && res.title_big && res.title_big.length > 10 ? 50 : 78;

  const buildCanvas = async () => {
    const wrapper = cardRef.current.parentElement;
    const prev = wrapper.style.transform;
    wrapper.style.transform = "none";
    try {
      return await html2canvas(cardRef.current, { backgroundColor: "#000000", useCORS: true, logging: false, scale: 1 });
    } finally {
      wrapper.style.transform = prev;
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const canvas = await buildCanvas();
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png", 1));
      const file = new File([blob], `madbar-${res.name}.png`, { type: "image/png" });
      const text = `${res.name} — ${res.total} PT · THE MAD BAR First Edition`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "THE MAD BAR", text });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `madbar-${res.name}.png`; a.click();
        URL.revokeObjectURL(url);
        toast.success("Immagine scaricata");
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error("Impossibile generare l'immagine");
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const canvas = await buildCanvas();
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png"); a.download = `madbar-${res.name}.png`; a.click();
      toast.success("Immagine scaricata");
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  const StatLine = ({ label, value, gold }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
      <span style={{ color: "#8E8E93", fontSize: 28, fontWeight: 800, letterSpacing: 1, fontFamily: "Inter, sans-serif" }}>{label}</span>
      <span style={{ color: gold ? BRAND : "#fff", fontSize: 34, fontWeight: 900 }}>{value}</span>
    </div>
  );

  const PREVIEW_W = 330;
  const scale = PREVIEW_W / 1080;
  const games = res ? [...res.games].sort((a, b) => a.game_number - b.game_number) : [];

  return createPortal(
    <div data-testid="share-modal" className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-black text-lg">Condividi risultato</h3>
          <button data-testid="share-close-btn" onClick={onClose} className="text-white"><X size={26} /></button>
        </div>

        <div className="mx-auto rounded-2xl overflow-hidden" style={{ width: PREVIEW_W, height: 1350 * scale }}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
            <div ref={cardRef} style={{ width: 1080, height: 1350, position: "relative", overflow: "hidden", background: "linear-gradient(165deg, #262626 0%, #050505 55%)", color: "#fff", boxSizing: "border-box", fontFamily: "Archivo, sans-serif" }}>
              {res && res.suit && (
                <div style={{ position: "absolute", top: 40, right: -40, fontSize: 640, lineHeight: 1, color: suitColor, opacity: 0.15, fontWeight: 900, pointerEvents: "none" }}>{SUIT_SYMBOL[res.suit]}</div>
              )}
              {res && res.suit && (
                <div style={{ position: "absolute", bottom: -30, left: -60, fontSize: 420, lineHeight: 1, color: suitColor, opacity: 0.10, fontWeight: 900, pointerEvents: "none" }}>{SUIT_SYMBOL[res.suit]}</div>
              )}
              <div style={{ height: 14, background: BRAND, width: "100%" }} />

              {!res ? (
                <div style={{ height: 1300, display: "flex", alignItems: "center", justifyContent: "center", color: "#8E8E93", fontSize: 40 }}>Caricamento…</div>
              ) : (
                <div style={{ position: "relative", padding: "56px 64px", height: 1336, boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <img src={`${process.env.PUBLIC_URL}/logo-white.png`} alt="" crossOrigin="anonymous" style={{ width: 210, height: 210, objectFit: "contain" }} />
                    <div style={{ color: BRAND, fontSize: 28, fontWeight: 900, letterSpacing: 5 }}>FIRST EDITION</div>
                  </div>

                  <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    {res.suit && (
                      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 10 }}>
                        <span style={{ color: suitColor, fontSize: 52, lineHeight: 1 }}>{SUIT_SYMBOL[res.suit]}</span>
                        <span style={{ color: "#8E8E93", fontSize: 32, fontWeight: 800, letterSpacing: 3, fontFamily: "Inter, sans-serif" }}>{SUIT_LABEL[res.suit]}</span>
                      </div>
                    )}

                    {isWinner ? (
                      <div style={{ background: BRAND, color: "#000", fontSize: bannerSize, fontWeight: 900, letterSpacing: 1, padding: "14px 26px", borderRadius: 18, textAlign: "center", lineHeight: 1.05, marginBottom: 16, whiteSpace: "nowrap", boxShadow: "0 0 60px rgba(245,212,0,0.45)" }}>{res.title_big}</div>
                    ) : res.title ? (
                      <div style={{ color: BRAND, fontSize: 46, fontWeight: 900, marginBottom: 10 }}>{res.title}</div>
                    ) : null}

                    <div style={{ fontSize: 74, fontWeight: 900, lineHeight: 1.0 }}>{res.name}</div>
                    {res.instagram && <div style={{ color: "#8E8E93", fontSize: 32, fontWeight: 600, marginTop: 4, fontFamily: "Inter, sans-serif" }}>@{res.instagram}</div>}

                    <div style={{ marginTop: 22, display: "flex", alignItems: "flex-end", gap: 16, lineHeight: 1 }}>
                      <span style={{ color: BRAND, fontSize: 150, fontWeight: 900, lineHeight: 1 }}>{res.total}</span>
                      <span style={{ color: "#fff", fontSize: 40, fontWeight: 900, letterSpacing: 3, paddingBottom: 18 }}>PT</span>
                    </div>
                    <div style={{ height: 7, width: 160, background: BRAND, borderRadius: 8, marginTop: 24 }} />

                    <div style={{ marginTop: 20 }}>
                      <StatLine label="RANKING WORKOUT" value={ord(res.workout_position)} />
                      {games.map((g) => (
                        <StatLine key={g.game_number} label={`GAME ${g.game_number}`} value={`${ord(g.position)} · ${g.points}pt`} />
                      ))}
                      {(res.is_finalist || res.final_position) ? (
                        <StatLine label="FINALE" value={ord(res.final_position)} gold={res.final_position >= 1 && res.final_position <= 3} />
                      ) : null}
                    </div>
                  </div>

                  <div style={{ color: "#8E8E93", fontSize: 26, fontWeight: 600, textAlign: "center", fontFamily: "Inter, sans-serif" }}>
                    08 · 11 · 2026 — {event?.location_name || "Palazzetto dello Sport, GE"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <button data-testid="share-download-btn" onClick={download} disabled={busy || !res} className="bg-surface2 text-onsurface rounded-2xl py-4 font-black flex items-center justify-center gap-2 disabled:opacity-60">
            <Download size={20} /> Scarica
          </button>
          <button data-testid="share-share-btn" onClick={share} disabled={busy || !res} className="bg-brand text-onbrand rounded-2xl py-4 font-black flex items-center justify-center gap-2 disabled:opacity-60">
            <Share2 size={20} /> {busy ? "..." : "Condividi"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
